import React, { useEffect, useState } from "react";
import { Menu, MenuItem, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ReactComponent as DownArrowIcon } from "../../../assets/icons/down-arrow.svg";
import { ReactComponent as NoFileIcon } from "../../../assets/icons/no-file.svg";

import InvoiceModal from "InvoiceModal";
import { downloadFile } from "utils/helpers.utils";
import { useInvoice } from "./hooks/useInvoice";
import { TextConstants } from "constants/TextConstants";
import { InvoiceDetail } from "SharedComponent/redux/sharedSlice.types";
import { isNotEmptyObject } from "utils/loadash.utils";
import { COLORS } from "config/theme/baseTheme";

const useStyles = makeStyles((theme) => ({
  menu: {
    borderRadius: "8px",
    minWidth: "100px",
    padding: "8px 0",
  },
  menuItem: {
    fontSize: "12px",
    padding: "8px 16px",
    color: theme.palette.primary.main,
  },
  dropdownButton: {
    textTransform: "none",
    fontSize: "12px",
    color: theme.palette.primary.main,
    background: "none",
    border: "none",
    padding: "0",
    margin: "0",
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  arrow: {
    width: "12px",
    height: "12px",
    marginTop: "2px",
  },
  noInvoiceMessage: {
    textAlign: "center",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "180px",
    whiteSpace: "normal",
    lineHeight: "1.5",
    color: COLORS.BLACK,
    "& .noInvoiceTitle": {
      fontWeight: "bold",
      fontSize: "14px",
      opacity: 0.8,
    },
    "& .noInvoiceDescription": {
      fontSize: "12px",
      opacity: 0.5,
    },
  },
  noInvoiceIcon: {
    marginBottom: "8px",
    fontSize: "24px",
    opacity: 0.5, 
  },
}));

interface ConsignmentDetailsProps {
  consignment: { consignmentCode: string };
}

const Invoice = ({ consignment }: ConsignmentDetailsProps) => {
  const {
    loading,
    invoiceData,
    displayInvoice,
    numPages,
    fetchInvoice,
    handleDocumentLoadSuccess,
  } = useInvoice();

  const [state, setState] = useState({
    anchorEl: null as HTMLElement | null,
    invoiceOptions: [] as InvoiceDetail[],
    selectedInvoice: null as string | null,
    invoiceConsignment: null as string | null,
    selectDownloadUrl: null as string | null,
    showInvoiceModal: false,
  });

  const classes = useStyles();

  useEffect(() => {
    if (isNotEmptyObject(invoiceData) && "consignmentCode" in invoiceData && "invoiceDetails" in invoiceData) {
      setState((prevState) => ({
        ...prevState,
        invoiceConsignment: invoiceData?.consignmentCode,
        invoiceOptions: invoiceData?.invoiceDetails,
      }));
    }
  }, [invoiceData]);

  const handleInvoiceClick = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setState((prevState) => ({ ...prevState, anchorEl: event.currentTarget }));
    if (state.invoiceConsignment !== consignment.consignmentCode) {
      const paramsInvoice = { consignmentNo: [consignment.consignmentCode] };
      fetchInvoice(paramsInvoice);
    }
  };

  const handleMenuClose = () => {
    setState((prevState) => ({ ...prevState, anchorEl: null }));
  };

  const handleInvoiceSelect = (invoice: InvoiceDetail) => {
    setState((prevState) => ({
      ...prevState,
      selectedInvoice: `data:application/pdf;base64,${invoice.fileContent}`,
      selectDownloadUrl: invoice.downloadUrl,
      showInvoiceModal: true,
    }));
  };

  const handleInvoiceModalClose = () => {
    setState((prevState) => ({
      ...prevState,
      showInvoiceModal: false,
      selectedInvoice: null,
    }));
  };

  const handleDownloadPdf = () => {
    if (state.selectDownloadUrl) {
      const fileName = `${consignment.consignmentCode}.pdf`;
      downloadFile(state.selectDownloadUrl, fileName);
    }
  };

  return (
    <>
      <Button
        className={classes.dropdownButton}
        onClick={handleInvoiceClick}
        aria-controls="invoice-menu"
        aria-haspopup="true"
        endIcon={<DownArrowIcon className={classes.arrow} />}
        disableRipple
        disableFocusRipple
      >
        {TextConstants.VIEW_INVOICE}
      </Button>
      <Menu
        id="invoice-menu"
        anchorEl={state.anchorEl}
        keepMounted
        open={Boolean(state.anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        getContentAnchorEl={null}
        classes={{ paper: classes.menu }}
      >
        {loading ? (
          <MenuItem disabled className={classes.menuItem}>
            {TextConstants.LOADING}
          </MenuItem>
        ) : state.invoiceOptions && state.invoiceOptions.length > 0 ? (
          state.invoiceOptions.map((invoice, index) => (
            <MenuItem
              key={`Invoice_${index + 1}`}
              onClick={() => handleInvoiceSelect(invoice)}
              className={classes.menuItem}
            >
              {`${TextConstants.INVOICE} ${index + 1}`}
            </MenuItem>
          ))
        ) : (
          <MenuItem  className={classes.menuItem} style={{ cursor: 'default',  pointerEvents: 'none' }}>
            <div className={classes.noInvoiceMessage}>
              <NoFileIcon className={classes.noInvoiceIcon} />
              <div className="noInvoiceTitle">{TextConstants.NO_INVOICE}</div>
              <div className="noInvoiceDescription">{TextConstants.NO_INVOICE_DESC}</div>
            </div>
          </MenuItem>
        )}
      </Menu>
      {state.showInvoiceModal && state.selectedInvoice && (
        <InvoiceModal
          open={state.showInvoiceModal}
          consignmentCode={consignment.consignmentCode}
          onClose={handleInvoiceModalClose}
          displayInvoice={displayInvoice}
          invoiceData={state.selectedInvoice}
          numPages={numPages}
          loading={loading}
          onDownload={handleDownloadPdf}
          onDocumentLoadSuccess={handleDocumentLoadSuccess}
        />
      )}
    </>
  );
};

export default Invoice;
